import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ArrowLeftShort, ArrowRightShort, ChevronLeft } from 'react-bootstrap-icons';

import type { IButton } from './types';

import { buttonType } from '@/styles/theme';
import { classList } from '@/helpers';
import { AddIcon, ChevronRight, ChevronRightWhite, CrossIcon } from '@/assets/icons';
import { Image } from './CommonComponents';

const Button = (props: IButton): React.ReactElement => {
  const { className, color, children, style = {}, type = 'button', size = 'l', disabled } = props;

  const btnColor = buttonType[color || 'primary'];

  return (
    <DefaultButton
      className={`${btnColor} ${size} ${className}`}
      style={style}
      type={type}
      onClick={props.onClick}
      disabled={disabled}
    >
      {children}
    </DefaultButton>
  );
};

const ButtonPrimary = (props: IButton): React.ReactElement => {
  return (
    <Button {...props} size={props.size} color={props.color} style={{ ...props.style }}>
      {props.label}
    </Button>
  );
};

const ButtonCancel = (props: IButton & { text?: string }): React.ReactElement => {
  const { t } = useTranslation();
  const style = { marginLeft: '16px', backgroundColor: '#fff' };

  return (
    <Button {...props} size="m" style={style}>
      {props.text ?? t`common.btnCancel`}
    </Button>
  );
};

const ButtonSave = (props: IButton): React.ReactElement => {
  const { t } = useTranslation();
  const style = { marginLeft: '16px', backgroundColor: '#0087dd' };

  return (
    <Button {...props} size="m" color="blue" style={style}>
      {props.label ? props.label : t`common.btnSave`}
    </Button>
  );
};

const ButtonApply = (props: IButton): React.ReactElement => {
  const { t } = useTranslation();
  const style = { marginLeft: '16px', backgroundColor: '#0A4599' };

  return (
    <Button {...props} size="m" color="blue" style={style}>
      {t`common.btnApply`}
    </Button>
  );
};

const ButtonOk = (props: IButton): React.ReactElement => {
  const { t } = useTranslation();
  const style = { marginLeft: '16px', backgroundColor: '#0A4599' };

  return (
    <Button {...props} size="m" color="blue" style={style}>
      {t`common.btnOk`}
    </Button>
  );
};

const ButtonClose = (props: IButton): React.ReactElement => {
  const { t } = useTranslation();
  const style = { marginLeft: '16px', backgroundColor: '#0A4599' };

  return (
    <Button {...props} size="m" color="blue" style={style}>
      {t`common.btnClose`}
    </Button>
  );
};

const StyledButtonBack = styled.button`
  background-color: #fff;
  color: #008acd;
  border-radius: 8px;
  padding: 8px 16px;
  width: 217px;
  height: 48px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.3);

  svg {
    margin-right: 0.5rem;
  }

  @media (max-width: 768px) {
    width: 150px;
  }
`;

const ButtonBack = (props: IButton): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <StyledButtonBack {...props} className={classList('btn', props.className)}>
      <ArrowLeftShort width="30px" height="30px" /> {t`common.btnBack`}
    </StyledButtonBack>
  );
};

const SmallerButtonBack = (props: IButton): React.ReactElement => {
  return (
    <StyledButtonBack {...props} className={classList('btn', props.className)}>
      <TextDecor>
        <ChevronLeft width="12px" height="12px" /> {props.label}
      </TextDecor>
    </StyledButtonBack>
  );
};

const StyledButtonClose = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const TextDecor = styled.span`
  font-weight: 600;
`;

const ButtonCloseIcon = (props: IButton): React.ReactElement => {
  return (
    <StyledButtonClose {...props} className={classList('btn', props.className)}>
      <Image className="ms-auto" width="14px" src={props.src || CrossIcon} alt={props.alt || 'close modal'} />
    </StyledButtonClose>
  );
};

const CustomButtonBackWithoutBorder = styled(ButtonBack)`
  box-shadow: none !important;
  text-align: left;
  padding: 0;
  margin-bottom: 5px;
  width: 100%;
`;

const StyledButtonNext = styled.button`
  background-color: #008acd;
  border-radius: 8px;
  padding: 8px 16px;
  width: 217px;
  height: 48px;

  svg {
    margin-right: 0.5rem;
  }

  @media (max-width: 768px) {
    width: 150px;
  }
`;

const ButtonNext = React.forwardRef(
  (props: IButton, ref: React.ForwardedRef<HTMLButtonElement>): React.ReactElement => {
    const { t } = useTranslation();

    return (
      <StyledButtonNext {...props} ref={ref} className={classList('btn btn-primary', props.className)}>
        {t`common.btnNext`} <ArrowRightShort width="30px" height="30px" />
      </StyledButtonNext>
    );
  },
);
ButtonNext.displayName = 'ButtonNext';

const ButtonAdd = (props: IButton): React.ReactElement => {
  return (
    <Button {...props} className={classList('btn btn-primary', props.className)}>
      <div className="d-flex align-items-center text-nowrap justify-content-center">
        <img src={AddIcon} className="me-2" />
        {props.children}
      </div>
    </Button>
  );
};

const ButtonSeeMore = (props: IButton): React.ReactElement => {
  return (
    <Button {...props} className={classList('btn btn-primary', props.className)}>
      <div className="d-flex align-items-center text-nowrap justify-content-center">
        {props.children}
        <img src={ChevronRight} className="ms-2" />
      </div>
    </Button>
  );
};

const CustomStyledButtonNext = styled.button`
  color: #008acd;
  background-color: #ffffff;
  border-color: #dadee1;
  border-radius: 8px;
  padding: 8px 16px;
  width: 100%;
  height: 48px;

  svg {
    margin-right: 0.5rem;
  }
`;

const ButtonMoreDetails = (props: IButton): React.ReactElement => {
  return (
    <CustomStyledButtonNext {...props} className={classList('btn btn-primary', props.className)}>
      <div className="d-flex align-items-center text-nowrap justify-content-center">
        {props.children}
        <img src={ChevronRight} className="ms-2" />
      </div>
    </CustomStyledButtonNext>
  );
};

const ButtonNextStep = (props: IButton): React.ReactElement => {
  return (
    <CustomStyledButtonNext
      {...props}
      className={classList('btn btn-primary', props.className)}
      disabled={!props.isButtonEnabled}
    >
      <div className="d-flex align-items-center text-nowrap justify-content-center">
        {props.children}
        <img src={ChevronRightWhite} className="ms-2" />
      </div>
    </CustomStyledButtonNext>
  );
};

interface IButtonProps {
  children: string;
  icon?: string;
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'reset' | 'submit';
  disabled?: boolean;
}

const ButtonClear = (props: IButtonProps): React.ReactElement => (
  <ClearButtonStyled onClick={props.onClick} className={props.className} type={props.type}>
    <ButtonContentWrapper className="button-content-wrapper">
      {props.icon && <img src={props.icon} className="button-content-icon" />}
      {props.children}
    </ButtonContentWrapper>
  </ClearButtonStyled>
);

const ClearButtonStyled = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

const ButtonBase = (props: IButtonProps): React.ReactElement => (
  <DefaultButton
    onClick={props.onClick}
    className={classList('btn btn-primary blue-outline', props.className)}
    type={props.type}
    disabled={props.disabled}
  >
    <ButtonContentWrapper className="button-content-wrapper">
      {props.icon && <img src={props.icon} className="button-content-icon" />}
      {props.children}
    </ButtonContentWrapper>
  </DefaultButton>
);

const ButtonContentWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: center;
  justify-content: center;

  img {
    margin-right: 0.5em;
  }
`;

export {
  Button,
  ButtonPrimary,
  ButtonClear,
  ButtonCancel,
  ButtonSave,
  ButtonApply,
  ButtonClose,
  ButtonBack,
  ButtonNext,
  ButtonOk,
  ButtonAdd,
  ButtonSeeMore,
  ButtonBase,
  CustomButtonBackWithoutBorder,
  ButtonMoreDetails,
  ButtonCloseIcon,
  SmallerButtonBack,
  ButtonNextStep,
};

const DefaultButton = styled.button`
  border-radius: 24px;
  border-style: solid;
  border-width: 1px;

  font-weight: 500;
  font-size: 16px;

  &[disabled] {
    opacity: 0.5;
  }

  &.blue {
    color: #fff;
    background-color: #0087dd;
    border: none;
  }
  &.blue-outline {
    color: #0087dd;
    background-color: #fff;
    border-color: #0087dd;
  }
  &.red {
    color: #fff;
    background-color: #eb0000;
    border: none;
  }
  &.red-outline {
    color: #eb0000;
    background-color: #fff;
    border-color: #eb0000;
  }

  &.s {
    height: 30px;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;

    img {
      height: 12px;
    }
  }
  &.m {
    height: 40px;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
  }
  &.l {
    height: 50px;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
  }
`;
